import { httpTokenCommon } from "../helper/http-common";
import { OrderData, ProductFilterParams } from "../types/saleOrder";

class SaleOrderServices {
  getSaleProductList(params: ProductFilterParams, customerId?: string) {
    const queryParams = customerId ? { ...params, customerId } : params;
    return httpTokenCommon.get("products/searchproducts", { params: queryParams });
  }
  getSaleOrderList(params: OrderData) {
    return httpTokenCommon.get("/Orders/search", { params });
  }
  postSaleOrder(inputData: OrderData) {
    return httpTokenCommon.post(`Orders`, inputData);
  }
  deleteSaleOrder(id: string) {
    return httpTokenCommon.delete(`Orders/${id}`, { silent: true });
  }
  /** COD order — POST /orders with paymentMethod=2 (CashOnDelivery) and customerId for SalesRep. */
  postCodSaleOrder(inputData: OrderData, customerId?: string) {
    return httpTokenCommon.post("Orders", {
      ...inputData,
      paymentMethod: 2,
      ...(customerId ? { customerId } : {}),
    });
  }
  getSaleOrderById(id: string) {
    return httpTokenCommon.get(`Orders/${id}`);
  }
  getCategoryList() {
    return httpTokenCommon.get(`products/categories`);
  }
  getOrderItemList() {
    return httpTokenCommon.get(`orders/getordersitems`);
  }
  getAllOrders(pageSize = 200) {
    return httpTokenCommon.get("/Orders/search", {
      params: { pageNumber: 1, pageSize, sortBy: "date", sortDescending: true },
    });
  }
  getProductById(id: string) {
    return httpTokenCommon.get(`products/${id}`, { silent: true });
  }

  /** Unscoped catalogue used only as a COD fallback, then filtered to the customer's site. */
  getRawProductCatalogue() {
    return httpTokenCommon.get("products", { silent: true });
  }
  getAllProducts(pageSize = 5000, customerId?: string) {
    return httpTokenCommon.get(`products/searchproducts`, {
      params: {
        keyword: "",
        pageNumber: 1,
        pageSize,
        sortDescending: false,
        ...(customerId ? { customerId } : {}),
      },
      silent: true,
    });
  }

  /** Fast catalog for dropdowns / name lookups (returns, workflow). Skips item-control stock calc. */
  getProductCatalog(pageSize = 5000, pageNumber = 1, customerId?: string) {
    return httpTokenCommon.get("products/catalog", {
      params: {
        keyword: "",
        pageNumber,
        pageSize,
        ...(customerId ? { customerId } : {}),
      },
      silent: true,
    });
  }
}

function readArrayField(
  payload: Record<string, unknown>,
  ...keys: string[]
): Record<string, unknown>[] {
  for (const key of keys) {
    const value = payload[key];
    if (Array.isArray(value)) return value as Record<string, unknown>[];
  }
  return [];
}

export function parseCatalogProducts(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== "object") return [];
  const payload = data as Record<string, unknown>;
  return readArrayField(payload, "items", "Items");
}

/** Order line items — same source the mobile app uses for return product names. */
export function parseOrderItems(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== "object") return [];
  const payload = data as Record<string, unknown>;
  return readArrayField(payload, "items", "Items");
}

/** Load every catalog page (API caps pageSize at 5000). */
export async function fetchAllCatalogProducts(
  customerId?: string
): Promise<Record<string, unknown>[]> {
  const pageSize = 5000;
  let pageNumber = 1;
  const all: Record<string, unknown>[] = [];
  let totalCount = Number.POSITIVE_INFINITY;

  while (all.length < totalCount) {
    const res = await saleOrderServices.getProductCatalog(pageSize, pageNumber, customerId);
    const payload = (res?.data ?? {}) as Record<string, unknown>;
    const pageItems = parseCatalogProducts(payload);
    all.push(...pageItems);

    const reportedTotal = Number(payload.totalCount ?? payload.TotalCount ?? 0);
    totalCount = reportedTotal > 0 ? reportedTotal : all.length;

    if (pageItems.length === 0 || pageItems.length < pageSize) break;
    pageNumber += 1;
  }

  return all;
}

const saleOrderServices = new SaleOrderServices();
export default saleOrderServices;
